import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { ApiObject, School, Program, StudyOption, IntakeDate } from 'app/types';
import Text from 'common/components/Text/Text';

import styles from 'features/explore/school/SchoolDetail.module.scss';
import { useMediaQuery } from 'react-responsive';
import { formatMonth, formatNumber, scheduleConversion } from 'common/utils/conversions';
import { capitalizeFirstLetter, entityIsProgram, entityIsSchool } from 'common/utils';

interface CompProps {
  entity?: School | Program;
  overviewRef?: React.RefObject<HTMLDivElement>;
  comesFromExplore?: boolean;
  belongsToProgramSchool?: boolean;
}

interface AttributeProps {
  attribute: string;
  value?: string;
  discount?: string;
}

const AttributeComponent = ({ attribute, value, discount }: AttributeProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <div className={styles.attributeContainer}>
      <div className={styles.blueBlock} />
      <div className={styles.attributeContent}>
        <Text size={isMobile ? 12 : 18} weight={600} inlineStyle={{ color: '#727D9B' }}>
          {attribute}
        </Text>
        <Text
          size={isMobile ? 12 : 16}
          weight={600}
          inlineStyle={{ display: 'flex', gap: isMobile ? '4px' : '8px', alignItems: 'center' }}
        >
          {value}
          {discount && (
            <div
              className={styles.discountContainer}
              style={{ padding: isMobile ? '2px 3px' : '4px 16px' }}
            >
              <Text size={isMobile ? 11 : 14} weight={500}>
                {discount}
              </Text>
            </div>
          )}
        </Text>
      </div>
    </div>
  );
};

const OverviewComponent = ({
  entity,
  overviewRef,
  comesFromExplore,
  belongsToProgramSchool,
}: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const programIntakeMonths = useMemo(
    () => [
      ...new Set(
        entityIsProgram(entity)
          ? entity?.study_options
              .map((study_option: StudyOption) => study_option.intake_dates)
              .flat()
              .map((intake: IntakeDate | undefined) => formatMonth(intake?.date))
          : []
      ),
    ],
    [entity]
  );
  return (
    <div
      className={styles.sectionContainer}
      ref={overviewRef}
      id={belongsToProgramSchool ? 'school-overview' : 'overview'}
    >
      <div className={styles.sectionTitle}>
        <div className={styles.blueBlock} />
        {comesFromExplore ? (
          <h3 className={styles.titleText}>Overview</h3>
        ) : (
          <h2 className={styles.titleText}>
            {belongsToProgramSchool ? 'Overview of program university' : 'Overview'}
          </h2>
        )}
      </div>
      <div className={styles.sectionContent}>
        {entity?.overview && (
          <Text size={isMobile ? 14 : 16} weight={400}>
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{entity?.overview}</ReactMarkdown>
          </Text>
        )}
        {entityIsSchool(entity) && (
          <div className={`${styles.sectionAttributes} row row-sm-6 row-lg-4`}>
            {entity?.intake_dates.length && (
              <AttributeComponent
                attribute={entity.intake_dates.length > 1 ? 'Intake months' : 'Intake month'}
                value={`${[
                  ...new Set(entity?.intake_dates.map((intake) => formatMonth(intake.date))),
                ].join(' / ')}`}
              />
            )}
            {entity?.city.cost_of_living && (
              <AttributeComponent
                attribute="Average living cost"
                value={`${entity?.city.country.currency} ${formatNumber(
                  entity?.city.cost_of_living
                )} / Year`}
              />
            )}
            {entity?.acceptance_rate && (
              <AttributeComponent
                attribute="Acceptance rate"
                value={capitalizeFirstLetter(entity?.acceptance_rate)}
              />
            )}
            {entity?.masters_tuition_range && (
              <AttributeComponent
                attribute="Tuition fee masters"
                value={entity?.masters_tuition_range}
              />
            )}
            {entity?.bachelors_tuition_range && (
              <AttributeComponent
                attribute="Tuition fee bachelors"
                value={entity?.bachelors_tuition_range}
              />
            )}
            {entity?.program_duration_range != null && (
              <AttributeComponent
                attribute="Program duration range"
                value={`${entity?.program_duration_range} months`}
              />
            )}
            <AttributeComponent attribute="Language" value={'English'} />
          </div>
        )}
        {entityIsProgram(entity) && (
          <div className={`${styles.sectionAttributes} row row-sm-6 row-lg-4`}>
            {entity?.study_options_first?.tuition_fee &&
              entity?.study_options_first.tuition_fee.amount_international !== null && (
                <AttributeComponent
                  attribute="Tuition fee"
                  value={
                    entity?.many_study_options
                      ? `From ${`${entity?.tuition_min_currency} `}${formatNumber(
                          entity?.tuition_min
                        )}`
                      : `${`${entity?.study_options_first.tuition_fee?.currency} `}
                          ${formatNumber(
                            entity?.study_options_first.tuition_fee?.amount_international
                          )}`
                  }
                />
              )}
            {entity?.program_duration_range != null && (
              <AttributeComponent
                attribute="Program duration"
                value={`${entity?.program_duration_range} months`}
              />
            )}
            {(entity?.study_options_first.schedule || entity?.multiple_schedules) && (
              <AttributeComponent
                attribute="Study mode"
                value={`${
                  entity?.multiple_schedules
                    ? 'full-time/part-time'
                    : scheduleConversion(entity?.study_options_first.schedule, true)
                }`}
              />
            )}
            {entity?.next_deadline && (
              <AttributeComponent attribute="Next deadline" value={entity?.next_deadline} />
            )}
            {programIntakeMonths.length && (
              <AttributeComponent
                attribute={programIntakeMonths.length > 1 ? 'Intake months' : 'Intake month'}
                value={`${programIntakeMonths.join(' / ')}`}
              />
            )}
            <AttributeComponent attribute="Language" value={'English'} />
          </div>
        )}
      </div>
    </div>
  );
};

export { AttributeComponent, OverviewComponent };
